import React from 'react';
import { Form, FormInstance } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { handleFormErrors } from '../../common/errorHelpers';
import { useGetClientEmployees } from '../../common/useGetClientEmployees';
import { FormDoubleInput } from '../../components/forms/FormDoubleInput';
import { FormInput } from '../../components/forms/FormInput';
import { StyledColumn } from '../../components/StyledColumn';
import { TableWithDataUpdate } from '../../components/TableWithDataUpdate';

import { useAddClientEmployee } from './useAddClientEmployee';
import { useDeleteClientEmployee } from './useDeleteClientEmployee';
import { useUpdateClientEmployee } from './useUpdateClientEmployee';

const mapErrorFieldsToFormLabels = {
  firstName: 'First name',
  lastName: 'Last name',
};

type ClientEmployeeRow = {
  key: string;
  uuid: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  contactData: {
    phoneNumber: string;
    email: string;
  };
};

type EmployeeFormValues = {
  key?: string;
  uuid?: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

const columns: ColumnsType<ClientEmployeeRow> = [
  {
    title: 'First name',
    dataIndex: 'firstName',
    width: '25%',
    sorter: (a, b) => a.firstName.localeCompare(b.firstName),
  },
  {
    title: 'Last name',
    dataIndex: 'lastName',
    width: '25%',
    sorter: (a, b) => a.lastName.localeCompare(b.lastName),
  },
  {
    title: 'Contact',
    dataIndex: 'contactData',
    width: '25%',
    render: (data: { email: string; phoneNumber: string }) => {
      return (
        <StyledColumn gap="0" style={{ marginTop: '-10px', marginBottom: '-10px' }}>
          <div>Phone: {data.phoneNumber}</div>
          <div>E-mail: {data.email}</div>
        </StyledColumn>
      );
    },
  },
];

const EmployeeForm: React.FC<{ form: FormInstance }> = ({ form }) => {
  return (
    <Form form={form} layout="vertical" colon={false}>
      <FormDoubleInput
        leftInput={<FormInput label="First name" name="firstName" required />}
        rightInput={<FormInput label="Last name" name="lastName" required />}
      />
      <FormDoubleInput
        leftInput={<FormInput label="Phone" name="phoneNumber" />}
        rightInput={<FormInput label="E-mail" name="email" />}
      />
    </Form>
  );
};

export const ClientEmployeesTable: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { data } = useGetClientEmployees(clientId);
  const [form] = Form.useForm();
  const updateClientEmployee = useUpdateClientEmployee();
  const addClientEmployee = useAddClientEmployee();
  const deleteClientEmployee = useDeleteClientEmployee();

  const dataSource = data
    ? data.map((employee) => {
        return {
          key: employee.uuid,
          uuid: employee.uuid,
          firstName: employee.firstName,
          lastName: employee.lastName,
          phoneNumber: employee.phoneNumber,
          email: employee.email,
          contactData: { phoneNumber: employee.phoneNumber, email: employee.email },
        };
      })
    : [];

  const employeeDataById = (id: string) => {
    return data?.find((item) => item.uuid === id);
  };

  const onSave = (record: EmployeeFormValues, successCallback?: () => void) => {
    const payloadData = { ...record };
    delete payloadData.key;
    if (record.uuid) {
      updateClientEmployee.mutate(
        {
          queryParams: { clientId, employeeId: record.uuid },
          data: { ...employeeDataById(record.uuid), ...payloadData },
        },
        {
          onSuccess: successCallback,
          onError: (error) => {
            handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
          },
        }
      );
    } else {
      addClientEmployee.mutate(
        {
          queryParams: { clientId },
          data: { ...payloadData },
        },
        {
          onSuccess: successCallback,
          onError: (error) => {
            handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
          },
        }
      );
    }
  };

  const onDelete = (record: ClientEmployeeRow) => {
    deleteClientEmployee.mutate({ queryParams: { clientId, employeeId: record.uuid } });
  };

  return (
    <TableWithDataUpdate<ClientEmployeeRow, EmployeeFormValues>
      title="Client employees"
      addButtonName="Add employee"
      columns={columns}
      dataSource={dataSource}
      itemForm={EmployeeForm}
      withEdit
      form={form}
      onSave={onSave}
      onDelete={onDelete}
    />
  );
};
